import {
    type ReactNode,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import styled from 'styled-components'

import { Button } from 'components/Button/Button'
import GroupCircles from 'components/UI/GroupCircles/GroupCircles'
import { RadioButton } from 'components-ref/atoms'
import { Modal } from 'components-ref/molecules/modal/modal'
import { Icons } from 'helpers/newPostIcons'
import { getProfileGroups } from 'services/endpoints/social'
import colors from 'styles/colors'
import { type IExtendedGroup, type IGroup } from 'types/interfaces/ITypes'

import FansList from '../FansList/FansList'

const useGroupsData = (_groups: IGroup[] | undefined) => {
    const [data] = useState<{ avatars: string[]; count: number }>({
        avatars: [],
        count: 0
    })

    return data
}

interface IGroupDropdownProps {
  preview?: IGroup[]
  groups?: IGroup[]
  apply: any
  hasFriends?: boolean
  groupsDisabled?: boolean
  headText?: string
  isEdit: boolean
  hasCloseButton?: boolean
  children?: ReactNode
  showEmptyGroups?: boolean
}
const GroupsDropdown = forwardRef<any, IGroupDropdownProps>(
  (
    {
      preview,
      groups,
      apply,
      headText,
      isEdit,
      hasCloseButton,
      children,
      showEmptyGroups = false
    },
    ref
  ) => {
        const [dropdownActive, setDropdownActive] = useState<boolean>(false)

        const [primaryGroups, setPrimaryGroups] = useState<IExtendedGroup[]>([])
        const [regularGroups, setRegularGroups] = useState<IExtendedGroup[]>([])

        const [previewReady, setPreviewReady] = useState<boolean>(false)

        const [selectedGroupsPrimary, setSelectedGroupsPrimary] = useState<
            IGroup[]
        >([])
        const [selectedGroupsRegular, setSelectedGroupsRegular] = useState<
            IGroup[]
        >([])

useImperativeHandle(ref, () => ({
    openDropdown() {
        setDropdownActive(true)
    }
}))
        const { avatars, count } = useGroupsData(preview)

        const { data: queriedGroups } = useQuery(
        'allGroups',
        getProfileGroups,
        {
            enabled: groups === undefined && dropdownActive,
            refetchOnWindowFocus: false,
            staleTime: 5000
        }
        )

        const data: IGroup[] = useMemo(() => {
        const source = groups ?? queriedGroups ?? []

        return source.filter(
            (group: IGroup) => group.name !== 'Limited Users'
        )
        }, [groups, queriedGroups])

        const hasAppliedDefault = useRef(false)

        const { t } = useTranslation()

        const toggleGroup = (id: string) => {
            if (!selectedGroupsRegular.some(group => group.id === id)) {
                // checking weather array contain the id.
                const tmpGroup = data?.filter((group: IGroup) => group.id === id)
                tmpGroup?.length &&
                    setSelectedGroupsRegular([
                        ...selectedGroupsRegular,
                        tmpGroup[0]
                    ])
            } else {
                const tmp = selectedGroupsRegular.filter(
                    group => group.id !== id
                )
                if (tmp.length === 0) {
                    setSelectedGroupsPrimary(primaryGroups)
                }
                setSelectedGroupsRegular(tmp)
            }
        }

        useEffect(() => {
            preview?.length && setPreviewReady(true)
        }, [preview])

        useEffect(() => {
            if (hasAppliedDefault.current) return

            if (isEdit) {
                if (!!preview?.length && previewReady) {
                    if (preview[0].primary) {
                        setSelectedGroupsPrimary([...preview])
                    } else {
                        setSelectedGroupsRegular([...preview])
                    }
                    hasAppliedDefault.current = true
                }
            } else {
                // FIX: data is always an array now, so check length instead of truthiness
                if (data.length) {
                    const defaultGroup = data.filter(
                        (group: IGroup) => group.name === 'All'
                    )
                    setSelectedGroupsPrimary([...defaultGroup])

                    if (preview && preview.length === 0) {
                        apply([...defaultGroup])
                    }

                    hasAppliedDefault.current = true
                }
            }

            // FIX: data is always an array now, so check length instead of truthiness
            if (data.length) {
                const extendedGroups: IExtendedGroup[] = data.map(
                    (group: IGroup) => {
                        let count = 0
                        let avatars: string[] = []
                        if (group.users) {
                            count = group.users.length
                            avatars = group.users.map(user => user.avatar.url)
                        }
                        return {
                            ...group,
                            count,
                            avatars
                        }
                    }
                )

                // only used when showEmptyGroups === false
                const allGroup = extendedGroups.find(g => g.name === 'All')
                const allHasZeroUsers = allGroup?.count === 0

                const tmpPrimary: IExtendedGroup[] = []
                const tmpRegular: IExtendedGroup[] = []

                extendedGroups.forEach((group: IExtendedGroup) => {
                    // CURRENT behavior (keep) unless StorySettings opts out
                    if (!showEmptyGroups) {
                        if (!allHasZeroUsers && group.count === 0) return
                    }

                    if (group.primary) tmpPrimary.push(group)
                    else tmpRegular.push(group)
                })

                setPrimaryGroups(tmpPrimary)
                setRegularGroups(tmpRegular)
            }
        }, [data, isEdit, previewReady, preview, showEmptyGroups, apply])

        return (
             <DropdownRoot>
                {children ?? (
<Head
  role='button'
  tabIndex={0}
  onClick={() => {
    if (isEdit) return
    setDropdownActive(true)
  }}
  onKeyDown={e => {
    if (isEdit) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setDropdownActive(true)
    }
  }}
>
                        <HeadIcon>
                            <img src={Icons.group} alt={t('addGroup')} />
                        </HeadIcon>
                        {preview?.length ? (
                            <HeadPreview>
                                <HeadNames>
                                    {preview.map((el, index) => (
                                        <p key={index}>
                                            {t(el.name)}
                                            {preview.length - 1 !== index
                                                ? ','
                                                : ''}
                                        </p>
                                    ))}
                                </HeadNames>
                                <GroupCircles
                                    group={{
                                        avatars,
                                        count
                                    }}
                                    size='36'
                                />
                            </HeadPreview>
                        ) : (
                            <HeadText>
                                {headText ?? t('selectAudience')}
                            </HeadText>
                        )}
                    </Head>
                )}
                {dropdownActive && (
                    <Modal
                        onClose={() => setDropdownActive(false)}
                        title={t('selectYourAudience')}
                        closeOnLayerClick={true}
                        isLayerTransparent={false}
                    >
                        <Subtitle>{t('controlWhoCanSeeYourPosts')}</Subtitle>
                        <List>
                            <>
                                {primaryGroups?.map(
                                    (group: IExtendedGroup, index: number) => {
                                        return (
                                            <ListItem key={index}>
                                                <FansListWrapper>
                                                    <FansList
                                                        title={t(group.name)}
                                                        fans={{
                                                            avatars:
                                                                group.avatars,
                                                            count: group.count
                                                        }}
                                                        hideOptions
                                                        clickFn={() => {
                                                            setSelectedGroupsPrimary(
                                                                [group]
                                                            )
                                                            !(
                                                                selectedGroupsRegular.length ===
                                                                0
                                                            ) &&
                                                                setSelectedGroupsRegular(
                                                                    []
                                                                )
                                                        }}
                                                        customClass='groupsDd__body__list__withRadio'
                                                    />
                                                </FansListWrapper>
                                                <RadioButtonWrapper>
                                                    <RadioButton
                                                        active={selectedGroupsPrimary.some(
                                                            el => {
                                                                return (
                                                                    el.id ===
                                                                    group.id
                                                                )
                                                            }
                                                        )}
                                                        clickFn={() => {
                                                            setSelectedGroupsPrimary(
                                                                [group]
                                                            )
                                                            !(
                                                                selectedGroupsRegular.length ===
                                                                0
                                                            ) &&
                                                                setSelectedGroupsRegular(
                                                                    []
                                                                )
                                                        }}
                                                    />
                                                </RadioButtonWrapper>
                                            </ListItem>
                                        )
                                    }
                                )}
                            </>
                            <Separator />
                            <>
                                {regularGroups?.map(
                                    (group: IExtendedGroup, index: number) => {
                                        return (
                                            <ListItem key={index}>
                                                <FansListWrapper>
                                                    <FansList
                                                        title={t(group.name)}
                                                        fans={{
                                                            avatars:
                                                                group.avatars,
                                                            count: group.count
                                                        }}
                                                        hideOptions
                                                        clickFn={() => {
                                                            toggleGroup(
                                                                group.id
                                                            )
                                                            !(
                                                                selectedGroupsPrimary.length ===
                                                                0
                                                            ) &&
                                                                setSelectedGroupsPrimary(
                                                                    []
                                                                )
                                                        }}
                                                    />
                                                </FansListWrapper>
                                                <RadioButtonWrapper>
                                                    <RadioButton
                                                        active={selectedGroupsRegular.some(
                                                            el => {
                                                                return (
                                                                    el.id ===
                                                                    group.id
                                                                )
                                                            }
                                                        )}
                                                        clickFn={() => {
                                                            toggleGroup(
                                                                group.id
                                                            )
                                                            !(
                                                                selectedGroupsPrimary.length ===
                                                                0
                                                            ) &&
                                                                setSelectedGroupsPrimary(
                                                                    []
                                                                )
                                                        }}
                                                    />
                                                </RadioButtonWrapper>
                                            </ListItem>
                                        )
                                    }
                                )}
                            </>
                        </List>
                        <ButtonsWrapper>
                            {hasCloseButton && (
                                <Button
                                    text={t('cancel')}
                                    color='grey'
                                    font='mont-14-normal'
                                    width='14'
                                    height='3'
                                    clickFn={() => {
                                        setDropdownActive(false)
                                    }}
                                />
                            )}
                            <Button
                                text={t('apply')}
                                color='black'
                                width='14'
                                height='3'
                                font='mont-14-normal'
                                clickFn={() => {
                                    !(selectedGroupsPrimary.length === 0) &&
                                        apply(selectedGroupsPrimary)
                                    !(selectedGroupsRegular.length === 0) &&
                                        apply(selectedGroupsRegular)
                                    setDropdownActive(false)
                                }}
                            />
                        </ButtonsWrapper>
                    </Modal>
                )}
            </DropdownRoot>
        )
    }
)

GroupsDropdown.displayName = 'GroupsDropdown'

export default GroupsDropdown

const Head = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    min-width: 100%;
    padding: 1rem;
    cursor: pointer;
`

const HeadPreview = styled.div`
    width: 85%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
`
const HeadNames = styled.div`
    display: flex;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: 'Montserrat', sans-serif;
    font-style: normal;
    color: ${colors.slateBlueGray};
    font-weight: 500;
    font-size: 1.2rem;
`

const HeadText = styled.div`
    font-family: 'Montserrat', sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 1.2rem;
    color: ${colors.slateBlueGray};
`

const HeadIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 3rem;
    height: 3rem;
    background: ${colors.black157deg};
    border-radius: 1rem;
    margin-right: 1rem;

    img {
        height: 1.6rem;
    }
`

const List = styled.ul`
    padding: 1rem 1rem 0 1rem;
    list-style: none;
    max-height: 28rem;
    overflow-y: auto;
`

const ListItem = styled.li`
    position: relative;
`
const DropdownRoot = styled.div`
  width: 100%;
  height: 100%;
`
const RadioButtonWrapper = styled.div`
    position: absolute;
    right: 1rem;
    top: calc(50% - 1rem);
`

const ButtonsWrapper = styled.div`
    display: flex;
    justify-content: space-around;
    align-items: center;
    width: 100%;
    padding-top: 2rem;
`

const FansListWrapper = styled.div`
    > div {
        padding-right: 4rem;
    }
`

const Separator = styled.div`
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    margin: 1.5rem 0;
`
const Subtitle = styled.h3`
    font-family: 'Montserrat', sans-serif;
    font-style: normal;
    font-weight: 500;
    letter-spacing: 0.02em;
    font-size: 1.4rem;
    line-height: 3.6rem;
    color: ${colors.grey};
    margin: 0 0 2.7rem 0;
    line-height: 120%;
`